"use client";

import { useState, useEffect } from "react";
import { Sun, Moon } from "lucide-react";

export default function ThemeToggle() {
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initialTheme = savedTheme || (systemPrefersDark ? "dark" : "light");
    setTheme(initialTheme);
    document.documentElement.setAttribute("data-theme", initialTheme);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    document.documentElement.setAttribute("data-theme", nextTheme);
  };

  if (!mounted) {
    return (
      <div className="theme-toggle-placeholder" aria-hidden="true" />
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className="theme-toggle-btn"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
    >
      {theme === "dark" ? (
        <Sun size={18} className="theme-icon anim-rotate-in" />
      ) : (
        <Moon size={18} className="theme-icon anim-rotate-in" />
      )}

      <style jsx>{`
        .theme-toggle-placeholder {
          width: 2rem;
          height: 2rem;
        }
        @media (min-width: 640px) {
          .theme-toggle-placeholder {
            width: 2.25rem;
            height: 2.25rem;
          }
        }
        .theme-toggle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: var(--radius-full);
          background-color: var(--control-bg);
          border: 1px solid var(--control-border);
          color: var(--text-primary);
          cursor: pointer;
          transition: all var(--transition-fast);
          padding: 0;
          min-width: 38px;
          min-height: 38px;
        }
        @media (min-width: 640px) {
          .theme-toggle-btn {
            width: 42px;
            height: 42px;
            min-width: 42px;
            min-height: 42px;
          }
        }
        .theme-toggle-btn:hover {
          background-color: var(--control-bg-hover);
          border-color: var(--control-border-hover);
          transform: scale(1.05);
        }
        .theme-icon {
          color: var(--text-primary);
        }
        @keyframes rotateIn {
          from {
            transform: rotate(-90deg);
            opacity: 0;
          }
          to {
            transform: rotate(0);
            opacity: 1;
          }
        }
        :global(.anim-rotate-in) {
          animation: rotateIn var(--transition-normal) forwards;
        }
      `}</style>
    </button>
  );
}
